import { FarmConfig, RewardToken } from '../types'

const hasRewardTokenChanged = (current: RewardToken, original: RewardToken): boolean => {
  return (
    current.tokenAddress !== original.tokenAddress ||
    current.rewardRate !== original.rewardRate ||
    current.rewardManager !== original.rewardManager
  )
}

export const hasConfigChanged = (current: FarmConfig | null, original: FarmConfig | null): boolean => {
  if (!current || !original) return false

  const hasRewardTokensChanged = (): boolean => {
    // If lengths are different, check the new tokens
    if (current.rewardTokens.length > original.rewardTokens.length) {
      const newTokens = current.rewardTokens.slice(original.rewardTokens.length)

      // All new tokens must have all fields filled to be considered a valid change
      return newTokens.every(
        (token) => token.rewardManager !== '' && token.rewardRate !== '' && token.tokenAddress !== ''
      )
    }

    // If length is same or less, check for changes in existing tokens
    return current.rewardTokens.some((token, index) => hasRewardTokenChanged(token, original.rewardTokens[index]))
  }

  // Check if any general fields have changed
  const hasGeneralFieldsChanged = (): boolean => {
    const generalFields: (keyof Omit<FarmConfig, 'rewardTokens'>)[] = [
      'rewardsDuration',
      'stakingTokenCap',
      'lockTimeMax',
      'lockTimeMin',
      'lockMultiplierMax',
    ]

    return generalFields.some((field) => {
      const currentValue = current[field]?.toString().trim()
      const originalValue = original[field]?.toString().trim()
      return currentValue !== originalValue && currentValue !== ''
    })
  }

  return hasRewardTokensChanged() || hasGeneralFieldsChanged()
}

export const getFieldError = (field: string, errors: any): string | undefined => {
  if (!errors) return undefined

  // Check for validation errors
  if (errors.validationErrors && Array.isArray(errors.validationErrors)) {
    const fieldError = errors.validationErrors.find((err: any) => err.field === field)
    if (fieldError) return fieldError.message
  }

  // Check for specific error
  if (errors.error && errors.error.field === field) {
    return errors.error.message
  }

  return undefined
}

/**
 * Checks if there are any validation errors in the errors object
 */
export const hasValidationErrors = (errors: any): boolean => {
  if (!errors) return false

  // Empty object check
  if (Object.keys(errors).length === 0) return false

  // Check for validation errors array
  if (errors.validationErrors && Array.isArray(errors.validationErrors) && errors.validationErrors.length > 0) {
    return true
  }

  // Check for specific error object
  if (errors.error && errors.error.type) {
    return true
  }

  return false
}
